// The World English Bible (public domain), a book at a time from /bible/web/<OSIS>.json (built by
// scripts/build-bible.mjs from eBible.org's USFM). The API never stores Scripture: it serves
// references, and the text comes from here (Votd, passage pages, the composer, plans).
// Each book is fetched once per page load; usePassage caches the result in TanStack Query too.
import { parsePassage } from "./books";
import { lookupPassage as sample } from "./passages";
import type { Passage } from "./types";

type BookJson = { book: string; name: string; translation: string; chapters: string[][] };

const books = new Map<string, Promise<BookJson | null>>();

function loadBook(osis: string): Promise<BookJson | null> {
  let p = books.get(osis);
  if (!p) {
    const base = (import.meta.env?.BASE_URL as string | undefined) ?? "/";
    p = fetch(`${base}bible/web/${encodeURIComponent(osis)}.json`)
      .then((r) => (r.ok ? (r.json() as Promise<BookJson>) : null))
      .catch(() => null);
    // A failed fetch isn't remembered, so the next lookup tries again.
    void p.then((b) => b ?? books.delete(osis));
    books.set(osis, p);
  }
  return p;
}

/** One chapter of the WEB, verse by verse (the Bible reader). Null when the book can't be fetched. */
export async function webChapter(osis: string, chapter: number): Promise<string[] | null> {
  const book = await loadBook(osis);
  return book?.chapters[chapter - 1] ?? null;
}

/**
 * A passage in the WEB: verses ("Phil 4:6-7", up to `maxVerses`) or a whole chapter ("Romans 8").
 * Falls back to the built-in sample when the book can't be fetched (offline, tests).
 */
export async function lookupWeb(reference: string, maxVerses = Infinity): Promise<Passage | null> {
  const p = parsePassage(reference, maxVerses);
  if (!p) return null;
  const book = await loadBook(p.book);
  if (!book) return p.chapterEnd === p.chapter ? sample(reference) : null;
  const chapters: NonNullable<Passage["chapters"]> = [];
  for (let c = p.chapter; c <= p.chapterEnd; c++) {
    const verses = book.chapters[c - 1];
    if (!verses) break;
    const from = c === p.chapter ? (p.from ?? 1) : 1;
    const to = c === p.chapterEnd ? (p.to ?? (p.chapterEnd === p.chapter ? (p.from ?? verses.length) : verses.length)) : verses.length;
    if (from > verses.length) break;
    chapters.push({
      n: c,
      verses: verses
        .slice(from - 1, Math.min(to, verses.length))
        .map((text, i) => ({ n: from + i, text }))
        .filter((v) => v.text),
    });
  }
  const text = chapters.flatMap((c) => c.verses.map((v) => v.text)).join(" ");
  if (!text) return null;
  // A chapter or more is read verse by verse; a verse or a few is quoted as one passage.
  const whole = p.from === null || p.chapterEnd > p.chapter;
  return { key: p.key, label: p.label, translation: "WEB", text, ...(whole ? { chapters } : {}) };
}
